import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Hammer, AlertTriangle, Truck } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { useChantiers, useCreateChantier, useUpdateChantier, useAllLots } from '@/hooks/useBetonCatalog';
import { useContacts } from '@/hooks/useCrm';
import {
  STATUT_CHANTIER_LABELS,
  type Chantier,
  type StatutChantier,
} from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { generateBLPDF } from '@/utils/beton-pdf';

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUT_BADGE: Record<StatutChantier, string> = {
  planifie: 'ds-bd ds-bd-n',
  en_cours: 'ds-bd ds-bd-i',
  suspendu: 'ds-bd ds-bd-w',
  termine:  'ds-bd ds-bd-s',
  annule:   'ds-bd ds-bd-d',
};

export const ChantiersPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutChantier | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Chantier | undefined>();
  const [blForChantier, setBlForChantier] = useState<Chantier | null>(null);

  const { data, isLoading, refetch } = useChantiers({ statut: statut || undefined, limit: 100 });
  const chantiers = data?.data ?? [];

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (c: Chantier) => { setEditing(c); setDialogOpen(true); };

  return (
    <>
      <Topbar
        title="Chantiers"
        subtitle={filiale ? `Suivi commercial & production · ${filiale.fullName}` : 'Chantiers'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}>
              <Plus size={14} /> Nouveau chantier
            </button>
          </>
        }
      />

      <div className="ds-content">
        <div className="ds-filters">
          <select
            className="ds-fi"
            value={statut}
            onChange={(e) => setStatut(e.target.value as StatutChantier | '')}
            style={{ width: 'auto' }}
          >
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_CHANTIER_LABELS) as StatutChantier[]).map((s) => (
              <option key={s} value={s}>{STATUT_CHANTIER_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Chantiers</div>
              <div className="ds-table-sub">{chantiers.length} chantier{chantiers.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Code</th>
                <th>Nom</th>
                <th>Client</th>
                <th className="ds-am">Volume</th>
                <th className="ds-am">Avancement</th>
                <th style={{ width: 100 }}>Début</th>
                <th style={{ width: 100 }}>Fin prévue</th>
                <th style={{ width: 90 }}>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && chantiers.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <Hammer size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun chantier</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}>
                      <Plus size={12} /> Créer le premier chantier
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && chantiers.map((c) => {
                const total = parseFloat(c.volumeTotal);
                const livre = parseFloat(c.volumeLivre);
                const pct = total > 0 ? Math.round((livre / total) * 100) : 0;
                return (
                  <tr key={c.id}>
                    <td><span className="ds-ref-code">{c.code}</span></td>
                    <td>
                      <div className="ds-entry-label">{c.nom}</div>
                      {c.adresse && <div className="ds-entry-sub">{c.adresse}</div>}
                    </td>
                    <td style={{ fontSize: 12 }}>{c.client?.companyName ?? c.client?.fullName ?? '—'}</td>
                    <td className="ds-am">{formatNumber(livre)} / {formatNumber(total)}</td>
                    <td className="ds-am">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                        <div style={{ width: 60, height: 5, background: 'var(--bg-deep)', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,var(--p),var(--p-light))' }} />
                        </div>
                        <span style={{ minWidth: 32, fontFamily: 'var(--font-m)', fontWeight: 700, color: 'var(--p)' }}>{pct}%</span>
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(c.dateDebut)}</td>
                    <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{fmtDate(c.dateFinPrevue)}</td>
                    <td><span className={STATUT_BADGE[c.statut]}>{STATUT_CHANTIER_LABELS[c.statut]}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => setBlForChantier(c)} className="ds-row-btn" title="Générer BL PDF" style={{ color: 'var(--p)' }}>
                          <Truck size={13} />
                        </button>
                        <button onClick={() => openEdit(c)} className="ds-row-btn" title="Modifier">
                          <Edit size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ChantierFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} chantier={editing} />
      <BLDialog chantier={blForChantier} onClose={() => setBlForChantier(null)} />
    </>
  );
};

// ── Dialog génération BL PDF ────────────────────────────────────

const BLDialog = ({ chantier, onClose }: { chantier: Chantier | null; onClose: () => void }) => {
  const open = chantier !== null;
  const filiale = useAuthStore((s) => s.filiale);
  const { data, isLoading } = useAllLots({ statut: 'valide', limit: 100 });
  const lots = data?.data ?? [];
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open) { setSelectedIds(new Set()); setError(null); } }, [open]);

  if (!chantier) return null;

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const onGenerate = async () => {
    setError(null);
    const selectedLots = lots.filter((l) => selectedIds.has(l.id));
    if (selectedLots.length === 0) { setError('Sélectionnez au moins un lot'); return; }
    if (!filiale)              { setError('Filiale inconnue'); return; }
    if (!chantier.client)       { setError('Le chantier doit avoir un client associé'); return; }

    const baseUrl = window.location.origin;
    const numeroBL = `BL-${chantier.code}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
    try {
      await generateBLPDF({
        numeroBL,
        dateEmission: new Date().toISOString().slice(0, 10),
        filiale:      { name: filiale.name, fullName: filiale.fullName, pays: filiale.pays ?? 'CI', devise: filiale.devise ?? 'FCFA' },
        client:       {
          fullName:    chantier.client.fullName,
          companyName: chantier.client.companyName ?? null,
        },
        chantierNom:     chantier.nom,
        chantierAdresse: chantier.adresse ?? undefined,
        lots:            selectedLots,
        baseUrl,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Bon de livraison — ${chantier.code}`} subtitle={`${chantier.nom} · sélectionner les lots à livrer`} size="lg">
      <div className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}
        {!chantier.client && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            ⚠ Ce chantier n'a pas de client CRM associé — éditez-le pour ajouter un client avant de générer un BL.
          </div>
        )}

        <div style={{ fontSize: 12, color: 'var(--tx-2)' }}>
          {lots.length} lot{lots.length > 1 ? 's' : ''} validé{lots.length > 1 ? 's' : ''} disponible{lots.length > 1 ? 's' : ''} · {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
        </div>

        {isLoading ? (
          <div className="text-center text-xs text-gray-500 py-4">Chargement des lots…</div>
        ) : lots.length === 0 ? (
          <div className="text-center text-xs text-gray-500 py-4">Aucun lot validé disponible. Validez des lots dans Production d'abord.</div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--bdr)', borderRadius: 6 }}>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th>N° Lot</th>
                  <th>Produit</th>
                  <th className="ds-am" style={{ width: 80 }}>Quantité</th>
                  <th style={{ width: 80 }}>Production</th>
                </tr>
              </thead>
              <tbody>
                {lots.map((l) => (
                  <tr key={l.id} onClick={() => toggle(l.id)} style={{ cursor: 'pointer', background: selectedIds.has(l.id) ? '#ecfdf5' : undefined }}>
                    <td><input type="checkbox" checked={selectedIds.has(l.id)} onChange={() => toggle(l.id)} /></td>
                    <td><span className="ds-ref-code">{l.lotNumber}</span></td>
                    <td style={{ fontSize: 12 }}>{l.ordre?.produit.code} — {l.ordre?.produit.designation}</td>
                    <td className="ds-am">{formatNumber(parseFloat(l.quantite))} {l.ordre?.produit.uniteVente}</td>
                    <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(l.dateProduction)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="button" className="ds-btn ds-btn-p" onClick={() => void onGenerate()} disabled={selectedIds.size === 0 || !chantier.client}>
            <Truck size={14} /> Générer BL PDF ({selectedIds.size})
          </button>
        </div>
      </div>
    </Dialog>
  );
};

// ── Dialog création/édition chantier ────────────────────────

const ChantierFormDialog = ({ open, onClose, chantier }: { open: boolean; onClose: () => void; chantier?: Chantier }) => {
  const isEdit = !!chantier;
  const [nom, setNom]                     = useState('');
  const [clientId, setClientId]           = useState('');
  const [adresse, setAdresse]             = useState('');
  const [volumeTotal, setVolumeTotal]     = useState('');
  const [volumeLivre, setVolumeLivre]     = useState('0');
  const [dateDebut, setDateDebut]         = useState('');
  const [dateFinPrevue, setDateFinPrevue] = useState('');
  const [statut, setStatut]               = useState<StatutChantier>('planifie');
  const [notes, setNotes]                 = useState('');
  const [error, setError]                 = useState<string | null>(null);

  const { data: contactsData } = useContacts({ contactType: 'client', limit: 200 });
  const contacts = contactsData?.data ?? [];
  const create = useCreateChantier();
  const update = useUpdateChantier();

  useEffect(() => {
    if (!open) return;
    if (chantier) {
      setNom(chantier.nom);
      setClientId(chantier.clientId ?? '');
      setAdresse(chantier.adresse ?? '');
      setVolumeTotal(chantier.volumeTotal);
      setVolumeLivre(chantier.volumeLivre);
      setDateDebut(chantier.dateDebut.slice(0, 10));
      setDateFinPrevue(chantier.dateFinPrevue?.slice(0, 10) ?? '');
      setStatut(chantier.statut);
      setNotes(chantier.notes ?? '');
    } else {
      setNom(''); setClientId(''); setAdresse('');
      setVolumeTotal(''); setVolumeLivre('0');
      setDateDebut(new Date().toISOString().slice(0, 10));
      setDateFinPrevue(''); setStatut('planifie'); setNotes('');
    }
    setError(null);
  }, [open, chantier]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        nom: nom.trim(),
        clientId:      clientId || undefined,
        adresse:       adresse.trim() || undefined,
        volumeTotal:   parseFloat(volumeTotal || '0'),
        volumeLivre:   parseFloat(volumeLivre || '0'),
        dateDebut,
        dateFinPrevue: dateFinPrevue || undefined,
        statut,
        notes:         notes.trim() || undefined,
      };
      if (isEdit && chantier) {
        await update.mutateAsync({ id: chantier.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const canSubmit = nom.trim() && parseFloat(volumeTotal || '0') > 0 && dateDebut;

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${chantier?.code}` : 'Nouveau chantier'} subtitle="Suivi commercial + production" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>
        )}

        <Field label="Nom du chantier *">
          <input value={nom} onChange={(e) => setNom(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="Ex: Tour Cocody Hauts" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Client">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Sélectionner —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.companyName ?? c.fullName}</option>
              ))}
            </select>
          </Field>
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutChantier)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_CHANTIER_LABELS) as StatutChantier[]).map((s) => (
                <option key={s} value={s}>{STATUT_CHANTIER_LABELS[s]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Adresse">
          <input value={adresse} onChange={(e) => setAdresse(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Lot, quartier, ville" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Volume total (m³) *">
            <input type="number" min="0.01" step="0.01" value={volumeTotal} onChange={(e) => setVolumeTotal(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="650" />
          </Field>
          <Field label="Volume déjà livré">
            <input type="number" min="0" step="0.01" value={volumeLivre} onChange={(e) => setVolumeLivre(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date début *">
            <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="ds-fi" required style={{ width: '100%' }} />
          </Field>
          <Field label="Date fin prévue">
            <input type="date" value={dateFinPrevue} onChange={(e) => setDateFinPrevue(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
        </div>

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="Contraintes, contacts terrain..." />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>
            {isEdit ? 'Enregistrer' : 'Créer le chantier'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
